// THE control table — every gameplay/menu action and its bindings, in one
// place (amoeba-input plan, bead monorail-w99). No rebind UI yet, but nothing
// outside this file may hardcode a key code or pad button index.
//
// Pad layout is Hades-informed: dash on A/Cross (the most-pressed action on
// the easiest button), attack on X/Square, and interact NEVER shares a combat
// button (Diablo 4's documented controller mistake). Triggers stay free for
// future aimed powers (hook, lash upgrades). Pad bindings sit inert until the
// gamepad device lands (bead monorail-whu).

import type { ActionDefs, Actions, Input } from '../engine/input'

const wasdArrows = {
  keyVector: {
    up: ['KeyW', 'ArrowUp'],
    down: ['KeyS', 'ArrowDown'],
    left: ['KeyA', 'ArrowLeft'],
    right: ['KeyD', 'ArrowRight'],
  },
}

export const CONTROLS = {
  buttons: {
    // J for WASD hands, Enter for arrow-key hands — the original monorail-w99 ask.
    attack: { context: 'gameplay', bufferMs: 120, bindings: [{ mouseButton: 0 }, { key: 'KeyJ' }, { key: 'Enter' }, { padButton: 2 }] },
    dash: { context: 'gameplay', bufferMs: 60, bindings: [{ key: 'Space' }, { padButton: 0 }] },
    shield: { context: 'gameplay', bindings: [{ key: 'ShiftLeft' }, { key: 'ShiftRight' }, { padButton: 1 }] },
    engulf: { context: 'gameplay', bindings: [{ key: 'KeyF' }, { padButton: 3 }] },
    portal: { context: 'gameplay', bindings: [{ key: 'KeyT' }, { padButton: 12 }] },
    // Contextual: opens the shop beside the Mother, pings the way home from afar.
    interact: { context: 'gameplay', bindings: [{ key: 'KeyC' }, { padButton: 5 }] },
    stats: { context: 'gameplay', bindings: [{ key: 'KeyI' }, { padButton: 9 }] },
    legend: { context: 'gameplay', bindings: [{ key: 'Slash' }, { padButton: 8 }] },
    // Predation reflex toggle: auto-attack while the aim assist holds a target.
    autoFire: { context: 'gameplay', bindings: [{ key: 'KeyR' }, { padButton: 11 }] }, // R3
    // Pickup card muscle memory: E wears (confirm), Q pockets (cancel).
    menuConfirm: { context: 'menu', bindings: [{ key: 'Enter' }, { key: 'KeyE' }, { padButton: 0 }] },
    menuCancel: { context: 'menu', bindings: [{ key: 'Escape' }, { key: 'KeyQ' }, { padButton: 1 }] },
  },
  vectors: {
    move: { context: 'gameplay', bindings: [wasdArrows, { stick: 'left' }] },
    // 'any': wheel zoom works while menus freeze the sim, so stick zoom must too.
    zoom: { context: 'any', bindings: [{ stick: 'right' }] },
    // In-run panel navigation (focus-nav.ts): arrows/WASD, left stick, or d-pad.
    menuNav: { context: 'menu', bindings: [wasdArrows, { stick: 'left' }, { padDpad: true }] },
  },
} satisfies ActionDefs<string, string>

export type GameButton = keyof typeof CONTROLS.buttons
export type GameVector = keyof typeof CONTROLS.vectors
export type GameActions = Actions<GameButton, GameVector>
export type GameInput = Input<GameButton, GameVector>

// Device-aware UI labels (Phase F, amoeba-input plan) — the single source
// legend/action-bar/pickup-card read for their key glyphs. Xbox-letter naming
// (A/B/X/Y) rather than brand glyphs: standard-mapping button indices don't
// reveal PlayStation vs. Xbox, and guessing wrong reads worse than a neutral
// label. Match the existing UI style: action-bar already shows 'SPACE'/'LMB'
// uppercase.
export const GLYPHS: Record<GameButton, { kbm: string; pad: string }> = {
  attack: { kbm: 'LMB·J', pad: 'X' },
  dash: { kbm: 'SPACE', pad: 'A' },
  shield: { kbm: 'SHIFT', pad: 'B' },
  engulf: { kbm: 'F', pad: 'Y' },
  portal: { kbm: 'T', pad: 'D-UP' },
  interact: { kbm: 'C', pad: 'RB' },
  stats: { kbm: 'I', pad: 'START' },
  legend: { kbm: '/', pad: 'SELECT' },
  autoFire: { kbm: 'R', pad: 'R3' },
  // ENTER is the advertised key (JRPG muscle memory); KeyE stays bound as the
  // silent hand-on-WASD alternate.
  menuConfirm: { kbm: 'ENTER', pad: 'A' },
  menuCancel: { kbm: 'Q', pad: 'B' },
}
